import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Search, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, isStaff, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => {
    const next = i18n.language?.startsWith("en") ? "fr" : "en";
    i18n.changeLanguage(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const linkCls = (path) =>
    `text-sm transition-colors hover:text-foreground ${
      location.pathname === path ? "text-foreground" : "text-muted-foreground"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-white/5 bg-black/80 backdrop-blur-xl" : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-6 px-6 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-1">
          <span className="font-heading text-xl font-black tracking-tight text-foreground">
            Vytali<span className="text-primary">Flix</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link to="/" data-testid="nav-home" className={linkCls("/")}>{t("nav.home")}</Link>
          <Link to="/browse" data-testid="nav-browse" className={linkCls("/browse")}>{t("nav.browse")}</Link>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden items-center md:flex">
            <div className="flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 focus-within:border-primary/50">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.search")}
                className="w-32 bg-transparent px-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:w-48"
              />
            </div>
          </form>

          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle"
            className="rounded-full border border-white/10 px-2.5 py-1 text-xs font-semibold uppercase text-foreground/80 transition-colors hover:border-primary/50"
          >
            {i18n.language?.startsWith("en") ? "EN" : "FR"}
          </button>

          {isStaff && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="hidden items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/25 md:flex"
            >
              <LayoutDashboard className="h-3.5 w-3.5" /> {t("nav.admin")}
            </Link>
          )}

          {user ? (
            <div className="hidden items-center gap-2 md:flex">
              <Link to="/account" data-testid="nav-account" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary">
                <User className="h-4 w-4" />
              </Link>
              <button onClick={logout} data-testid="nav-logout" className="text-muted-foreground transition-colors hover:text-foreground">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden rounded-full bg-foreground px-4 py-1.5 text-sm font-semibold text-background transition-opacity hover:opacity-90 md:block"
            >
              {t("nav.login")}
            </Link>
          )}

          <button className="md:hidden" onClick={() => setMenuOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-white/5 bg-black/95 px-6 py-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full bg-transparent px-2 text-sm outline-none" />
          </form>
          <div className="flex flex-col gap-3">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-sm">{t("nav.home")}</Link>
            <Link to="/browse" onClick={() => setMenuOpen(false)} className="text-sm">{t("nav.browse")}</Link>
            {isStaff && <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-sm text-primary">{t("nav.admin")}</Link>}
            {user ? (
              <>
                <Link to="/account" onClick={() => setMenuOpen(false)} className="text-sm">{t("nav.myspace")}</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-sm text-muted-foreground">{t("nav.logout")}</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="text-sm text-primary">{t("nav.login")}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
